import { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Ship } from 'lucide-react';
import { Toaster, toast } from 'sonner';
import { useMember } from '../context/MemberContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const emptyForm = { name: '', ship_class: '', role: '', manufacturer: '', crew_size: '' };
const statusBadge = { pending: 'badge-pending', approved: 'badge-active', rejected: 'badge-pending' };

const Field = ({ label, children }) => (
    <div>
        <label className="form-label">{label}</label>
        {children}
    </div>
);

export const MemberVesselRegister = ({ shipClasses }) => {
    const { member, getAuthHeaders } = useMember();
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState(emptyForm);
    const [mine, setMine] = useState([]);
    const [saving, setSaving] = useState(false);

    const fetchMine = async () => {
        try {
            const res = await axios.get(`${API}/member/vessels`, getAuthHeaders());
            setMine(res.data);
        } catch (error) {
            console.error('Failed to fetch your vessels:', error);
        }
    };

    useEffect(() => { if (member) fetchMine(); }, [member]);

    if (!member) return null;

    const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.ship_class) {
            toast.error('Vessel name and class are required');
            return;
        }
        setSaving(true);
        try {
            await axios.post(`${API}/member/vessels`, form, getAuthHeaders());
            toast.success('Vessel submitted for approval');
            setForm(emptyForm);
            setOpen(false);
            fetchMine();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to submit vessel');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="mb-8 tactical-panel p-4" data-testid="member-vessel-register">
            <Toaster position="top-right" theme="dark" />
            <div className="flex items-center justify-between gap-4">
                <div>
                    <h3 className="font-heading font-bold text-white flex items-center gap-2"><Ship className="w-4 h-4 text-red-500" /> My Vessels</h3>
                    <p className="font-mono text-xs text-gray-500 mt-1">Register your ships — fleet command reviews each submission.</p>
                </div>
                <button onClick={() => setOpen(true)} className="btn-primary py-2 px-4 flex items-center gap-2 text-xs" data-testid="register-vessel-button">
                    <Plus className="w-4 h-4" /> Register Vessel
                </button>
            </div>
            {mine.length > 0 && (
                <div className="mt-4 space-y-2" data-testid="my-vessels-list">
                    {mine.map((v) => (
                        <div key={v.id} className="flex items-center justify-between border-t border-white/5 pt-2" data-testid={`my-vessel-${v.id}`}>
                            <span className="text-white text-sm">{v.name} <span className="font-mono text-xs text-gray-500 ml-2">{v.ship_class}</span></span>
                            <span className={`badge ${statusBadge[v.approval_status] || 'badge-active'}`}>{v.approval_status || 'approved'}</span>
                        </div>
                    ))}
                </div>
            )}
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="bg-[#0a0a0a] border border-white/10 text-white">
                    <DialogHeader>
                        <DialogTitle className="font-heading">Register Vessel</DialogTitle>
                        <DialogDescription className="font-mono text-xs text-gray-500">Submitted ships appear in the Hangar once an admin approves them.</DialogDescription>
                    </DialogHeader>
                    <form onSubmit={submit} className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <Field label="Vessel Name"><input className="form-input" value={form.name} onChange={set('name')} data-testid="member-vessel-name" /></Field>
                            <Field label="Class">
                                <select className="form-input" value={form.ship_class} onChange={set('ship_class')} data-testid="member-vessel-class">
                                    <option value="" disabled>Select class</option>
                                    {shipClasses.map((sc) => <option key={sc.id} value={sc.name}>{sc.name}</option>)}
                                </select>
                            </Field>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <Field label="Manufacturer"><input className="form-input" value={form.manufacturer} onChange={set('manufacturer')} data-testid="member-vessel-manufacturer" /></Field>
                            <Field label="Role"><input className="form-input" value={form.role} onChange={set('role')} data-testid="member-vessel-role" /></Field>
                        </div>
                        <Field label="Crew Size"><input className="form-input" value={form.crew_size} onChange={set('crew_size')} data-testid="member-vessel-crew" /></Field>
                        <div className="flex gap-3 pt-2">
                            <button type="button" onClick={() => setOpen(false)} className="btn-secondary flex-1 py-2" data-testid="member-vessel-cancel">Cancel</button>
                            <button type="submit" disabled={saving} className="btn-primary flex-1 py-2" data-testid="member-vessel-submit">{saving ? 'Submitting...' : 'Submit For Approval'}</button>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
};
